/* eslint-disable max-len */
import React from 'react';
import { TFunction } from 'i18next';
import { GetTitle } from 'pages/EstimatePage/TableSection/tableComponents';
import { Flex, Text, WithTooltip } from 'UI';

export interface PublicationPeriod {
  publicationStart: string;
  publicationEnd: string;
}

export interface PublicationTargetedAudience {
  dealerTypes: string;
  fileName: string;
}

export interface TitleColumnOptions {
  key?: string;
  dataCy?: string;
}

export function createTitleColumn(t: TFunction, options: TitleColumnOptions = {}) {
  const { key, dataCy = 'promotion-title-value' } = options;
  return {
    title: GetTitle(t('backoffice.promotion.publication_preview.promotion_title', 'Promotion title')),
    dataIndex: 'title',
    ...(key === undefined ? {} : { key }),
    render: function renderTitle(title: string) {
      return (
        <Flex maxWidth={150} justify={'center'} align={'center'} data-cy={dataCy}>
          <WithTooltip title={title}>
            <Text type={'text'} ellipsis>
              {title}
            </Text>
          </WithTooltip>
        </Flex>
      );
    },
  };
}

export interface PeriodColumnOptions {
  title?: React.ReactNode;
  key?: string;
  dataCy?: string;
  width?: number;
}

export function createPeriodColumn(t: TFunction, options: PeriodColumnOptions = {}) {
  const { title, key, dataCy = 'period-value', width } = options;
  return {
    title: title ?? GetTitle(t('backoffice.promotion.publication_preview.period', 'Period')),
    dataIndex: 'period',
    ...(key === undefined ? {} : { key }),
    ...(width === undefined ? {} : { width }),
    render: function renderPeriod(period: PublicationPeriod) {
      const periodTooltipText = `${period.publicationStart} - ${period.publicationEnd}`;
      return (
        <Flex minWidth={90} direction={'column'} justify={'center'} align={'center'} data-cy={dataCy}>
          <WithTooltip title={periodTooltipText}>
            <Text type={'text'}>{period.publicationStart}</Text>
          </WithTooltip>
          <WithTooltip title={periodTooltipText}>
            <Text type={'text'}>{period.publicationEnd}</Text>
          </WithTooltip>
        </Flex>
      );
    },
  };
}

export interface TargetColumnOptions {
  key?: string;
  dataCy?: string;
  separator?: string;
}

export function createTargetColumn(t: TFunction, options: TargetColumnOptions = {}) {
  const { key, dataCy = 'target-value', separator = ' ' } = options;
  return {
    title: GetTitle(t('backoffice.promotion.target', 'Target')),
    dataIndex: 'targetedAudience',
    ...(key === undefined ? {} : { key }),
    render: function renderTarget(targetedAudience: PublicationTargetedAudience) {
      const targetedAudienceToolTipTitle = `${targetedAudience.dealerTypes}${separator}${targetedAudience.fileName}`;
      return (
        <Flex direction={'column'} justify={'center'} align={'center'} data-cy={dataCy}>
          <WithTooltip title={targetedAudienceToolTipTitle}>
            <Text type={'text'}>{targetedAudience.dealerTypes}</Text>
          </WithTooltip>
          <WithTooltip title={targetedAudienceToolTipTitle}>
            <Text type={'text'}>{targetedAudience.fileName}</Text>
          </WithTooltip>
        </Flex>
      );
    },
  };
}
